import { useEffect } from "react"
import {
  Links,
  Meta,
  Outlet,
  redirect,
  Scripts,
  ScrollRestoration,
  useLocation,
  useRouteLoaderData,
} from "react-router"
import type {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router"

import tailwindStyles from "./tailwind.css?url"
import fontCss from "./fonts.css?inline"
import radioCanadaBigLatin from "./fonts/radio-canada-big-normal-400-700-latin.woff2?url"
import { initPosthog } from "./client/posthog.client"
import { welcomeRedirectUrl } from "./lib/unacast"
import {
  GOOGLE_ADS_TAG_ID,
  googleTagSnippet,
  wantsGoogleTag,
} from "./lib/googleTag"
import {
  canonicalUrl,
  OG_IMAGE,
  pageMeta,
  SITE_DESCRIPTION,
  SITE_TITLE,
  SITE_URL,
} from "./lib/meta"
import { AnnouncementBanner } from "./components/landing/AnnouncementBanner"
import { InstantRouteScroll } from "./components/InstantRouteScroll"

// Legacy unacast.com hosts point their DNS at this server. Requests arriving
// on such a host are sent to the hand-off page instead of being served pine59
// content under the old domain. This loader covers every matched route; the
// $.tsx catch-all covers URLs that match nothing (loaders don't run on 404s).
//
// It also decides, per request, whether this visitor gets the Google tag:
// US visitors only, based on the region header the load balancer stamps on
// the request (see lib/googleTag.ts). Layout reads the flag back through
// useRouteLoaderData so the snippet is only ever present in US HTML.
export async function loader({ request }: LoaderFunctionArgs) {
  const welcome = welcomeRedirectUrl(request.url)
  if (welcome) throw redirect(welcome)
  return { googleTag: wantsGoogleTag(request) }
}

// Default metadata — used for the home page and as the fallback for any route
// that doesn't export its own `meta`. Every other page composes its tags with
// pageMeta() from ~/lib/meta so the social card never gets dropped.
export const meta: MetaFunction = ({ location }) =>
  pageMeta({
    title: SITE_TITLE,
    description: SITE_DESCRIPTION,
    pathname: location.pathname,
  })

// Brand type system (from the Figma design language):
//   Instrument Serif  -> H1 / poetic display
//   Radio Canada Big  -> H2-H4, paragraphs, nav
//   Geist Mono        -> buttons, captions
//   Roboto Flex       -> small captions (font-flex)
// The faces are self-hosted (app/fonts, declared in app/fonts.css) instead
// of linked from fonts.googleapis.com: that stylesheet was a render-blocking
// cross-origin request that Lighthouse charged ~0.8 s for on mobile, and
// self-hosting lets us preload the one face the hero paints with (Radio Canada
// Big; the mono face first appears several sections down, so preloading it
// would only compete with the LCP image).
//
// The @font-face rules themselves are inlined into <head> (see Layout) rather
// than linked: as a stylesheet they were one more render-blocking round trip
// on slow 4G (~150 ms) and put the woff2 files a hop further down the
// critical path. Vite still rewrites the url()s to hashed /assets paths.
export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwindStyles },
  { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
  {
    rel: "preload",
    href: radioCanadaBigLatin,
    as: "font",
    type: "font/woff2",
    crossOrigin: "anonymous",
  },
]

// Structured data, helps both search engines and AI answer engines (GEO)
// associate the new brand with concrete, extractable facts from day one.
// A @graph lets us ship Organization + WebSite + FAQPage in one script.
const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": `${SITE_URL}/#organization`,
      name: "Pine59",
      url: SITE_URL,
      logo: `${SITE_URL}/brand/logo-full.svg`,
      description: SITE_DESCRIPTION,
      foundingLocation: "Oslo, Norway",
      areaServed: ["United States", "Norway", "Sweden", "Finland", "Estonia"],
      knowsAbout: [
        "foot traffic data",
        // kept as a discoverability keyword (search term) even though brand
        // voice now says "insights", per the SEO call
        "location intelligence",
        "aggregated location intelligence",
        "trade area analysis",
        "human mobility data",
        "origin-destination matrices",
      ],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: SITE_URL,
      name: "Pine59",
      publisher: { "@id": `${SITE_URL}/#organization` },
    },
    {
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is Pine59?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Pine59 provides aggregated location intelligence that helps organizations understand how places and populations change and perform. We transform aggregated location statistics into actionable insights for analytics, products and AI.",
          },
        },
        {
          "@type": "Question",
          name: "I'm an existing Unacast customer, what changes?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "If your Unacast contract is for our aggregated location intelligence (the Aggregated Location Insights products now operating as Pine59), it stays exactly as it is, there's nothing to re-sign. Two practical things change: payments move to a new Pine59 bank account (you'll receive updated payment details), and your logins, data feeds and API move to new pine59.com addresses such as app.pine59.com and console.pine59.com, so update your bookmarks and saved endpoints. Same team, same methodology, same data, under a new name.",
          },
        },
        {
          "@type": "Question",
          name: "What data does Pine59 provide?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Pine59 delivers statistical insights into foot traffic, visit length, trade areas and trips across locations, neighborhoods, cities and infrastructure. Access our data through web applications, APIs or data delivery.",
          },
        },
        {
          "@type": "Question",
          name: "Is Pine59 privacy compliant?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Pine59 is built around statistical insights about places and populations, not people. Signals are aggregated by our data partners before they reach Pine59, with GDPR and CCPA compliance built into our operating model.",
          },
        },
        {
          "@type": "Question",
          name: "Where does Pine59 have coverage?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Pine59 currently covers the United States and the Nordics: Norway, Sweden, Finland and Estonia, with the UK coming soon. Our coverage keeps expanding.",
          },
        },
        {
          "@type": "Question",
          name: "Can we switch to Pine59 from another provider?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Share the use case and the locations that matter, and the Pine59 team suggests the best way to test them side by side with the current provider before anything is decided. Trends, comparisons and rankings are the fair test, since absolute counts differ between methodologies.",
          },
        },
      ],
    },
  ],
}

export function Layout({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  // Per-page canonical (the static `links` export can't see the path, so every
  // page would otherwise canonicalise to the homepage). Same helper as og:url.
  const canonical = canonicalUrl(pathname)
  // Undefined when Layout renders an error boundary or the root loader threw.
  const rootData = useRouteLoaderData<typeof loader>("root")
  const googleTag = rootData?.googleTag === true
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        {/* Self-hosted @font-face rules, inlined (see the links() comment). */}
        <style dangerouslySetInnerHTML={{ __html: fontCss }} />
        <Links />
        <link rel="canonical" href={canonical} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </head>
      <body>
        {children}
        {/* must stay directly before ScrollRestoration, see the component */}
        <InstantRouteScroll />
        <ScrollRestoration />
        <Scripts />
        {/* First-party lead attribution (see attribution.server.ts). Two
            cookies: p59_ft = FIRST touch (set once), p59_lt = LAST touch
            (rewritten whenever a visit arrives with campaign params or an
            external referrer). Each stores referrer, landing path, utm_*,
            gclid/li_fat_id. The get-started action reads them to stamp
            "came from LinkedIn / Google Ads / organic / direct" onto the
            lead in HubSpot, Slack, Linear and PostHog — no third-party
            tracker involved, and the CRM stays swappable. Inline so it runs
            even if the app bundle fails. */}
        <script
          dangerouslySetInnerHTML={{
            __html: `(function(){try{
var P=new URLSearchParams(location.search),d={r:document.referrer||"",p:location.pathname,t:Date.now()},c=false;
["utm_source","utm_medium","utm_campaign","utm_content","utm_term","gclid","gbraid","wbraid","msclkid","fbclid","igshid","ttclid","twclid","li_fat_id"].forEach(function(k){var v=P.get(k);if(v){d[k]=v;c=true}});
function base(h){return h.split(".").slice(-2).join(".")}
var ext=false;try{ext=!!d.r&&base(new URL(d.r).hostname)!==base(location.hostname)}catch(e){}
var sec=location.protocol==="https:"?";Secure":"";
function set(n){document.cookie=n+"="+encodeURIComponent(JSON.stringify(d))+";path=/;max-age=7776000;SameSite=Lax"+sec}
if(document.cookie.indexOf("p59_ft=")<0)set("p59_ft");
if(c||ext)set("p59_lt");
}catch(e){}})();`,
          }}
        />
        {/* Google tag, US visitors only, injected after load + idle. Not a
            conversion tag: conversions stay on the gclid upload (see
            docs/google-ads-conversions.md). Gives Google Ads its conversion
            linker cookie and remarketing. Non-US HTML never contains it. */}
        {googleTag && (
          <script
            dangerouslySetInnerHTML={{
              __html: googleTagSnippet(GOOGLE_ADS_TAG_ID),
            }}
          />
        )}
      </body>
    </html>
  )
}

export default function App() {
  useEffect(() => {
    // Init at hydration, not deferred to load/idle: capture() has no queue, so
    // a click or a bounce before init would lose the event and the landing
    // $pageview (with its UTM attribution). posthog-js is a dynamic import
    // anyway, so it never blocks the initial paint.
    initPosthog()
  }, [])

  // Site-wide announcement bar at the top of every page. It sits in normal
  // flow and scrolls away; the Header pins to the top once it's past. On the
  // launch post itself — the bar's own "Read more" destination — the bar drops
  // its link (see AnnouncementBanner) so it isn't a dead end.
  return (
    <>
      <AnnouncementBanner />
      <Outlet />
    </>
  )
}
